import { z } from 'zod';
/**
 * IBAN italiano — validation.
 *
 * Formato IT:
 *   IT kk C AAAAA BBBBB CCCCCCCCCCCC   (27 char totali)
 *      kk = check digits (MOD 97-10)
 *      C  = CIN (lettera)
 *      AAAAA = ABI (5 cifre)
 *      BBBBB = CAB (5 cifre)
 *      CCCCCCCCCCCC = conto (12 alfanumerico)
 *
 * Algoritmo checksum MOD 97-10 (ISO 13616):
 *   1. Sposta i primi 4 caratteri in coda.
 *   2. Sostituisci ogni lettera con numero: A=10, B=11, ..., Z=35.
 *   3. Calcola il mod 97 della stringa numerica risultante.
 *   4. Il risultato deve essere 1.
 *
 * V1: solo IBAN italiani (prefix IT).
 */
const IT_IBAN_LENGTH = 27;
const IT_IBAN_REGEX = /^IT[0-9]{2}[A-Z][0-9]{10}[A-Z0-9]{12}$/;
/** Converte una stringa alfanumerica in stringa numerica secondo ISO 13616. */
function ibanLettersToDigits(s) {
    let out = '';
    for (const ch of s) {
        if (ch >= '0' && ch <= '9') {
            out += ch;
        }
        else if (ch >= 'A' && ch <= 'Z') {
            out += String(ch.charCodeAt(0) - 'A'.charCodeAt(0) + 10);
        }
        else {
            throw new Error(`Invalid IBAN character: '${ch}'`);
        }
    }
    return out;
}
/** Calcola `n mod 97` per una stringa numerica arbitrariamente lunga. */
function mod97(numeric) {
    let remainder = 0;
    for (const ch of numeric) {
        remainder = (remainder * 10 + Number(ch)) % 97;
    }
    return remainder;
}
/** Verifica il checksum IBAN (qualsiasi paese). */
export function isValidIbanChecksum(iban) {
    const normalized = iban.replace(/\s+/g, '').toUpperCase();
    if (normalized.length < 5)
        return false;
    const rearranged = normalized.slice(4) + normalized.slice(0, 4);
    try {
        return mod97(ibanLettersToDigits(rearranged)) === 1;
    }
    catch {
        return false;
    }
}
/** Parse/validate un IBAN italiano. */
export function parseIbanIT(raw) {
    const normalized = raw.replace(/\s+/g, '').toUpperCase();
    if (normalized.length === 0) {
        return { valid: false, reason: 'IBAN obbligatorio', normalized };
    }
    const country = normalized.slice(0, 2);
    if (country !== 'IT') {
        return {
            valid: false,
            reason: `Solo IBAN italiani supportati in V1 (ricevuto prefisso '${country}')`,
            normalized,
            country,
        };
    }
    if (normalized.length !== IT_IBAN_LENGTH) {
        return {
            valid: false,
            reason: `IBAN italiano deve essere di ${IT_IBAN_LENGTH} caratteri (ricevuti ${normalized.length})`,
            normalized,
            country,
        };
    }
    if (!IT_IBAN_REGEX.test(normalized)) {
        return { valid: false, reason: 'Formato IBAN italiano non valido', normalized, country };
    }
    if (!isValidIbanChecksum(normalized)) {
        return { valid: false, reason: 'Checksum IBAN non valido', normalized, country };
    }
    return { valid: true, normalized, country };
}
/**
 * Zod schema per IBAN italiano.
 * L'output è l'IBAN normalizzato (uppercase, no spazi).
 */
export const ibanSchema = z
    .string({ required_error: "L'IBAN è obbligatorio" })
    .trim()
    .min(1, { message: "L'IBAN è obbligatorio" })
    .transform((raw) => raw.replace(/\s+/g, '').toUpperCase())
    .superRefine((iban, ctx) => {
    const parsed = parseIbanIT(iban);
    if (!parsed.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: parsed.reason ?? 'IBAN non valido',
        });
    }
});
/**
 * Branded variant of {@link ibanSchema} dedicated to addebito Agenzia delle Entrate (AdE).
 * Same runtime validation as `ibanSchema`, but the inferred type carries a `'IbanAdE'`
 * brand that prevents accidental cross-assignment with `BillingProfile.ibanEncrypted`
 * (see BR-3 — separazione billing IBAN / addebito AdE).
 */
export const ibanAdeSchema = ibanSchema.brand();
//# sourceMappingURL=iban.js.map